import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/services/supabaseClient";
import type { Tables, InsertTables, UpdateTables } from "@/types/database";

async function fetchTechnicalSheetByEventId(eventId: string) {
  const { data, error } = await supabase
    .from("technical_sheets")
    .select("*")
    .eq("event_id", eventId)
    .single();

  if (error && error.code !== "PGRST116") throw error;
  return data;
}

async function createTechnicalSheet(sheet: InsertTables<"technical_sheets">) {
  const { data, error } = await supabase
    .from("technical_sheets")
    .insert([sheet])
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updateTechnicalSheet(
  id: string,
  updates: UpdateTables<"technical_sheets">,
) {
  const { data, error } = await supabase
    .from("technical_sheets")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export function useTechnicalSheet(eventId: string) {
  return useQuery({
    queryKey: ["technical_sheet", eventId],
    queryFn: () => fetchTechnicalSheetByEventId(eventId),
    enabled: !!eventId,
  });
}

export function useCreateTechnicalSheet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTechnicalSheet,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["technical_sheet", data.event_id],
      });
      queryClient.invalidateQueries({ queryKey: ["event", data.event_id] });
    },
  });
}

export function useUpdateTechnicalSheet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateTables<"technical_sheets">;
    }) => updateTechnicalSheet(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["technical_sheet", data.event_id],
      });
      queryClient.invalidateQueries({ queryKey: ["event", data.event_id] });
    },
  });
}

export type TechnicalSheet = Tables<"technical_sheets">;
